"use client";

// Helpers for CONTACT segments (a saved question over the address book).
// Note: lib/segments.ts is something else entirely — that's the SMS message-segment calculator.

import type { ContactSegment, SegmentFilter } from "@/lib/types";

export const EMPTY_FILTER: SegmentFilter = {
  groupId: "",
  messaged: "any",
  days: "30",
  attrKey: "",
  attrValue: ""
};

// One place that turns a filter into query params, so the live preview and the saved segment can
// never disagree about what the filter means.
export function filterToParams(f: SegmentFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.groupId) p.set("groupId", f.groupId);
  if (f.messaged === "never") p.set("neverMessaged", "true");
  if (f.messaged === "not-in" && f.days) p.set("notMessagedDays", f.days);
  if (f.messaged === "within" && f.days) p.set("messagedWithinDays", f.days);
  if (f.attrKey.trim() && f.attrValue.trim()) {
    p.set("attrKey", f.attrKey.trim());
    p.set("attrValue", f.attrValue.trim());
  }
  return p;
}

// The same filter as a POST body for saving a segment.
export function filterToBody(f: SegmentFilter) {
  return {
    groupId: f.groupId ? Number(f.groupId) : null,
    neverMessaged: f.messaged === "never",
    notMessagedDays: f.messaged === "not-in" && f.days ? Number(f.days) : null,
    messagedWithinDays: f.messaged === "within" && f.days ? Number(f.days) : null,
    attrKey: f.attrKey.trim() || null,
    attrValue: f.attrValue.trim() || null
  };
}

export function segmentToFilter(s: ContactSegment): SegmentFilter {
  return {
    groupId: s.groupId ? String(s.groupId) : "",
    messaged: s.neverMessaged
      ? "never"
      : s.notMessagedDays != null
        ? "not-in"
        : s.messagedWithinDays != null
          ? "within"
          : "any",
    days: String(s.notMessagedDays ?? s.messagedWithinDays ?? 30),
    attrKey: s.attrKey ?? "",
    attrValue: s.attrValue ?? ""
  };
}

// Plain-English summary of what a segment asks — shown on the segment cards.
export function describeSegment(s: ContactSegment): string {
  const parts: string[] = [s.groupName ? `in “${s.groupName}”` : "all contacts"];
  if (s.neverMessaged) parts.push("never messaged");
  else if (s.notMessagedDays != null) parts.push(`not messaged in ${s.notMessagedDays} days`);
  else if (s.messagedWithinDays != null) parts.push(`messaged in the last ${s.messagedWithinDays} days`);
  if (s.attrKey && s.attrValue) parts.push(`${s.attrKey} = ${s.attrValue}`);
  return parts.join(" · ");
}
